/**
 * 联网搜索 provider 的**唯一真相源**：合法值域 + 推荐链序 + 「下一条该加谁」。
 *
 * 住在 shared 而不是 core，是因为消费方跨了依赖方向的两端：
 *  · `@dosia/core` —— 运行时按这份值域过滤策展下发的链、按这份序做兜底；
 *  · `apps/admin-portal-web` —— 超管配链的下拉选项与「+ 添加」按钮（该 app **只**依赖
 *    shared，不依赖 core，且 core 是 Node/Electron-main 侧的包，不该进浏览器 bundle）；
 *  · `packages/shared` 自己 —— `PlatformToolCuration.webSearchProviders` 的类型与
 *    `buildPlatformToolCuration` 的落库清洗白名单。
 *
 * 收进来之前，同一份清单在仓里有**四份副本**：core 的 `WebSearchProviderId` 联合、
 * shared types 里 `webSearchProviders` 的内联联合、model-registry 的
 * `HOSTED_WEB_SEARCH_PROVIDERS`、以及 admin 页面自己又写了一遍的局部 type。加一条腿要在
 * 四处各改一次，而漏掉任何一处都**不是编译失败**——漏 types 就是「超管配得出、类型说不
 * 合法」，漏 registry 就是「配了但落库时被静默丢掉」，漏 admin 就是「下拉里根本没有」。
 * 三种都表现为「配了没生效」，且都查不到报错（架构不变量 #9）。
 */
/**
 * 全部合法 provider id，**顺序即推荐链序**。
 *
 * 排序判据不是单价表，是「这条腿失败时谁在买单、失败粒度多粗」：
 *  1. `deepseek_web` —— 质量最好（唯一答对当期模型并给权威来源）、唯一如实上报搜索次数。
 *     成本：北京时间 2026-11-01 00:00 起 DeepSeek 对模型代搜按次收费（每次计费搜索 ¥0.005，
 *     https://api-docs.deepseek.com/guides/search ），代搜本身又是一次按 token 计费的模型调用。按一次生产
 *     实抓样本（`packages/core/src/tools/__tests__/fixtures/wire-deepseek-anthropic-websearch.json`：
 *     未命中缓存的输入 13,404 token、另有缓存命中 640、输出 819、计费搜索 2 次）与官方价目估算，deepseek-flash
 *     代搜一次员工搜索约 ¥0.027（空闲）/ ¥0.043（高峰）；同样的量用 v4-pro 代搜约 ¥0.081 / ¥0.153，所以主脑
 *     同家腿的 DeepSeek 一律由 flash 代搜（{@link HOSTED_WEB_SEARCH_BRAIN_SEARCH_MODEL}，N-185）。单样本
 *     估算、不是账单。此前这里写的「$0.0024/次」是代搜还不收搜索费时的口径。
 *  2. `glm_web`     —— 同为**按 token 计费**的网关组腿（公司买单，不吃任何人的订阅额度），
 *     单价与 deepseek 同档（线上 manifest 实读：$0.15/$0.50 per 1M vs deepseek-v4-flash 的
 *     $0.14/$0.28），所以与 deepseek 同列在订阅腿之前。
 *     排 deepseek **之后**而不是之前：deepseek 排第一的判据是质量实测，那条实测没有被
 *     重做，不能凭「glm 也便宜」把它顶下来。（一条**未采信**的线索：glm 的结果块给
 *     `page_content`，deepseek 给的是不透明的 `encrypted_content` → glm 有摘要而 deepseek
 *     恒空。这只说明 glm 的**载荷更全**，不等于搜得更准；要动第 1 位得先做头对头实测。）
 *  3. `grok_web` / 4. `openai_web` —— 吃**订阅额度**，与员工自己的 Codex / Grok 工作抢同
 *     一份配额。这份机会成本不出现在单价表里，却是这两条腿真实的价格，所以垫底。
 *     `openai_web` 更后：ChatGPT 是**周级**封顶（实证：gpt-plus-03 从 08-13 被限到
 *     08-20），失败粒度最粗、打满代价最大。
 *  5. `tavily`       —— 专职搜索 API（不是大模型代搜），经**网关代理**打
 *     `/v1/search`（REQ-268 起）：凭证从超管策展填的 gateway 组解出，用量记在网关。
 *     排最后是历史实测：它曾长期 432 配额耗尽；换成网关托管后额度由网关侧统一管，
 *     但「大模型代搜给得出结构化来源」这条质量判据没有被重做，所以不动它的位次。
 *
 * ## Anthropic 面为什么是这两条腿（deepseek + glm），不是三条
 *
 * 本期原计划接三条（deepseek / glm / claude），**网关侧 2026-08-28 的生产实抓把另外两条
 * 否掉了**；其中 `glm` 的缺口 08-29 已修复并复验通过，重新进链。判据不是「这个组能不能
 * 搜到」，而是「返回的块能不能按 Anthropic hosted 契约解析出结构化来源」——`claude` 至今
 * 卡在这条判据上，且它的失败**不报错**：
 *
 *  · `deepseek` ✅ 标准形状：`web_search_tool_result` → `web_search_result{title, url,
 *    encrypted_content, page_age}`。摘要恒空（给的是 `encrypted_content` 不是
 *    `page_content`），但「title+url 无摘要」在这条链上本来就是常态——Grok 的
 *    `action.sources[]` 连 title 都没有——所以不影响它排第一。
 *
 *  · `glm_web` ✅ **2026-08-29 本仓独立实抓复验通过**（不是采信网关侧报告——恢复条件的
 *    判据方是 DOSIA 侧，所以形状由本仓自己打探针确认）。生产 `0acde3098`，
 *    `glm-default` 组 + `glm-5.3-flash`，`/v1/messages` + `web_search_20250305`：
 *      - `server_tool_use.name = "web_search"`（不再是 `web_search_prime`）
 *      - `web_search_tool_result.content` 是**结构化数组**（不再是 Python repr 字符串），
 *        条目 `type:"web_search_result"`，字段 `{type, title, url, page_content}`
 *      - 全文零 `'link':`（repr 特征）——即旧的「骗过零来源守卫」形态已消失
 *    **流式与非流式两条腿分别抓、分别确认**：它们在网关侧是两条独立接线，此前的缺陷正是
 *    「只接了一条」，用其中一条的结果代表两条会重新踩进同一个坑。两条的块序列与条目字段
 *    一致（流式 `content_block_start` 里同样是 `server_tool_use ×2` +
 *    `web_search_tool_result ×2`）。
 *    网关随后前进到 `f8d94b48f`，**没有采信「那条改动不影响本路径」的说法，而是照同一套
 *    探针又打了一次**：两条腿形状逐字未变。
 *    它给 `page_content`，所以是 Anthropic 面唯一**带摘要**的一条。
 *
 *  · `claude_web` ❌ 见下。
 *
 * ### 曾经的 `glm_web` 缺陷长什么样（留档：这是这份清单存在要防的形态）
 *
 * 修复前网关把上游 GLM 的结果**原样透传**成 `tool_result`（不是 `web_search_tool_result`），
 * 且 `content` 是一个 **Python repr 字符串**而非结构化 JSON——
 * `"[{'text': [{'title': …, 'link': …, 'content': …}]}]"`，字段名也不同（`link` 而非 `url`）。
 * **比「解析不出来」更糟的是它会骗过零来源守卫**：守卫判据是「零 results **且** 正文无
 * URL」，而那个 repr 字符串会被 `collectTextParts` 收进 `rawText`，里面带着
 * `'link': 'https://…'`，正则一命中就认为「有来源」。于是 glm 会以**成功**返回一个
 * `results: []` 的 payload，**链就停在那里**，永远走不到下面真能给结构化来源的
 * grok/openai——正是一期 #2793 要堵的「交付一段没有出处的散文」换了个入口。
 * 当时刻意**不**在本仓解析那个 repr 字符串：那是给网关的归一化缺口打补丁，属于自建对齐。
 * 归一化改在了网关侧，本仓的解析逻辑一个字没改就认得新形状——这正是当初拒绝打补丁买到的。
 *
 * ## 为什么没有 `claude_web`（本期刻意不做，不是漏了）
 *
 * 它一度排在第三位（$0.00087、质量好）。**这个前提被实测推翻了**：网关侧 2026-08-28 对
 * `claude-lite`（账号 `claude-lite-packy-aws`）走 Anthropic 面 `web_search_20250305`
 * **连测三次全部退化**——返回的是 `tool_use` 而不是 `server_tool_use`，即 hosted 工具
 * 根本没被服务端执行，模型把它当普通函数工具调了。同账号同模型同请求体，确定性复现，
 * 不是偶发。
 *
 * 疑似原因（**未证实，别当结论引用**）：账号名里的 `aws` 指向 AWS Bedrock，而 Anthropic
 * 官方文档写明 web search 在 Bedrock 上不可用；packy 是中转池，可能把请求路由到了不同
 * 后端。网关侧看不到 packy 内部路由，所以只有「claude-lite 现在搜不了」这一条是确定的。
 *
 * 因此 `claude_web` **整条不进值域**——留一个选得中却搜不出来源的下拉项，正是这份清单存在
 * 要防的「配了没生效」，而它的失败形态恰好是最难查的那种（HTTP 200、链静默变短或静默
 * 降级、零报错）。
 *
 * 要恢复一条腿，两步缺一不可：
 *  ① **本仓自己**打探针复测到契约形状（claude 要 `server_tool_use` 而非 `tool_use`），
 *     **流式与非流式各抓一次**——它们在网关侧是独立接线，一条通不代表两条通；
 *  ② 把 id 加回本数组，并把 {@link WEB_SEARCH_PROVIDERS_IN_PREFERENCE_ORDER} 派生出的
 *     那些 `Record` 各补一格。**加腿要填哪几格，单源在 `@dosia/core` 的
 *     `web-search-mcp-tool.ts` 头注**，而最终判据是 `pnpm typecheck` 的报错。
 *     这里**刻意不复述格子数**——那是同一事实的第二份副本，必漂移（`glm_web` 那次就是：
 *     清单在施工中途从 8 张涨到 9 张，抄在这里的数字当场就过期了）。只记住一件事：
 *     **不是「改动是一行」**，且少任何一格都是编译失败而不是运行时静默——那正是当初把
 *     值域从四份副本收成一处时要买的东西。
 *
 * ①「本仓自己复测」不是形式：网关侧的实测报告对本仓是**二手的**，而恢复条件的判据方是
 * DOSIA 侧——契约由消费方验收（`glm_web` 08-29 的恢复就是照这条走的）。
 *
 * **真正的链序权威在超管策展**（`PlatformToolCuration.webSearchProviders` 有序），这份
 * 只是推荐序与合法值域，不是第二个真相源——运行时永远按策展给的顺序走。
 */
export const WEB_SEARCH_PROVIDERS_IN_PREFERENCE_ORDER = [
    "deepseek_web",
    "glm_web",
    "grok_web",
    "openai_web",
    "tavily",
];
export function isWebSearchProviderId(v) {
    return (typeof v === "string" &&
        WEB_SEARCH_PROVIDERS_IN_PREFERENCE_ORDER.includes(v));
}
export function isHostedWebSearchProviderId(v) {
    return isWebSearchProviderId(v) && v !== "tavily";
}
/**
 * 托管搜索腿 → 网关 `family_slug`（N-136 主脑同家腿的**单源**）。
 *
 * 业务含义：员工在企业版选的主脑如果属于这几家之一，联网搜索先走「主脑那一家、同一个网关组」
 * 的托管搜索（代搜模型默认是主脑这一个，见 {@link HOSTED_WEB_SEARCH_BRAIN_SEARCH_MODEL}），失败再按
 * 超管链往下。键 = 托管腿，值 = 网关清单行上的 `family_slug`（取值域与
 * pi 选线家族表 `PI_WIRE_PREFERENCE_BY_FAMILY` 同一套 slug）。
 *
 * 刻意是 `Record<HostedWebSearchProviderId, …>`：加一条托管腿时这里少一格 = 编译失败，逼加腿的人
 * 表态「它对应哪个家族」。反向（家族 → 腿）从这张表派生（{@link hostedWebSearchProviderForFamily}），
 * 不另写第二张表。没有同家托管搜索的家族（kimi / qwen / mimo / doubao / anthropic / google）不在
 * 值里，主脑同家腿对它们不生效。
 */
export const HOSTED_WEB_SEARCH_PROVIDER_FAMILIES = {
    deepseek_web: "deepseek",
    glm_web: "glm",
    grok_web: "grok",
    openai_web: "openai",
};
/**
 * 主脑家族（网关 `family_slug`）→ 它自家的托管搜索腿；该家族没有同家托管搜索 → `undefined`。
 * 入参是清单里的不可信字符串：先 trim，只认表里的值（原型链上的名字不算）。
 */
export function hostedWebSearchProviderForFamily(familySlug) {
    const slug = typeof familySlug === "string" ? familySlug.trim() : "";
    if (!slug)
        return undefined;
    for (const provider of WEB_SEARCH_PROVIDERS_IN_PREFERENCE_ORDER) {
        if (provider === "tavily")
            continue;
        if (HOSTED_WEB_SEARCH_PROVIDER_FAMILIES[provider] === slug)
            return provider;
    }
    return undefined;
}
/**
 * 主脑同家腿**由哪个模型代搜**（N-185，维护者 2026-10-27 拍板「改用便宜的搜索」）。键 = 托管腿，值 = 该家族
 * 代搜固定用的模型 id；不在表里的腿用主脑这一个模型（N-136 原样）。
 *
 * 业务：DeepSeek 从北京时间 2026-11-01 00:00 起对模型代搜按次收费，代搜本身又是一次按 token 计费的模型调用；
 * 搜到的结果来自同一个 DeepSeek 搜索后端，用 v4-pro（token 单价约是 flash 的 4.5 倍）代搜不会搜得更准。所以
 * DeepSeek 家族的主脑一律由 deepseek-flash 代搜——按家族而不是只认 v4-pro 一个 id，以后再出的贵模型同样生效；
 * 主脑本来就是 flash 时与 N-136 逐字相同。超管链的 `deepseek_web` 不点名模型、按更短的 id 挑行，挑到的本来
 * 就是 deepseek-flash，两条腿由此一致。
 *
 * 只换「哪个模型」，不换组、不放宽行：代搜仍只用主脑组的凭证、只认保证执行 `web_search` 的行；主脑组里这个
 * 模型的搜索面行不保证执行，就是没有这条腿（不退回让主脑自己代搜）；组里根本没有它的搜索面行，才按 N-136 的
 * 既有规则退到同组同家族行（desktop `resolveHostedSearchEndpoint`，按更短的 id 挑，组里只剩 v4-pro 时挑到的
 * 就是它）。
 * 退场条件：DeepSeek 搜索统一改走独立搜索接口（N-186）。
 */
export const HOSTED_WEB_SEARCH_BRAIN_SEARCH_MODEL = {
    deepseek_web: "deepseek-flash",
};
/** 主脑同家腿实际代搜用的模型 id：{@link HOSTED_WEB_SEARCH_BRAIN_SEARCH_MODEL} 点名了就用它，否则是主脑这一个模型。 */
export function hostedWebSearchBrainSearchModel(provider, brainModelId) {
    return HOSTED_WEB_SEARCH_BRAIN_SEARCH_MODEL[provider] ?? brainModelId;
}
/**
 * 「再加一条 provider 的话，该加哪条」—— 推荐序里第一条尚未被使用的；全用完了回落
 * `tavily`（重复行是合法的：同一个 provider 可以挂不同 group 排在链的不同位置）。
 *
 * 给 admin 页面的「+ 添加」按钮用。放在这里而不是组件里：它就是上面那份推荐序的直接
 * 消费者，写进组件等于把顺序抄第二份进另一个包，而组件里的逻辑既没有单测、也不进
 * typecheck 的语义面——改错了要靠人点开页面才看得见。
 */
export function nextRecommendedWebSearchProvider(used) {
    const seen = new Set(used);
    return WEB_SEARCH_PROVIDERS_IN_PREFERENCE_ORDER.find((id) => !seen.has(id)) ?? "tavily";
}
//# sourceMappingURL=web-search-providers.js.map